"use strict";
import { useQuery } from "@tanstack/react-query";
import { Token } from "@uniswap/sdk-core";
import { useMemo } from "react";
import { useTopazPoolFeeDetailsByAddress } from "components/lists/items/pools/useTopazPoolFeeDetailsByAddress";
import { OnchainItemListOptionType } from "components/lists/items/types";
import { TICK_SPACING_TO_FEE, getBscSubgraphs } from "constants/bscContracts";
import { getContractAbi, getContractAddress } from "constants/contracts";
import { getTokenLogoUrl } from "constants/tokenLogos";
import { useTokenLists } from "contexts/TokenListsContext";
import { ProtocolVersion } from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { UniverseChainId } from "features/chains/types";
import { fetchFromSubgraph } from "utils/fetchSubgraph";
import { isAddress } from "utilities/src/addresses";
import { logger } from "utilities/src/logger/logger";
import { createPublicClient, http } from "viem";
import { bsc } from "viem/chains";
const V2_POOL_SEARCH_QUERY = `
  query SearchV2Pools($searchText: String!, $first: Int!) {
    pairs(
      first: $first
      orderBy: reserveUSD
      orderDirection: desc
      where: {
        or: [
          { token0_: { symbol_contains_nocase: $searchText } }
          { token0_: { name_contains_nocase: $searchText } }
          { token1_: { symbol_contains_nocase: $searchText } }
          { token1_: { name_contains_nocase: $searchText } }
        ]
      }
    ) {
      id
      token0 {
        id
        symbol
        decimals
        name
      }
      token1 {
        id
        symbol
        decimals
        name
      }
      reserveUSD
      stable
    }
  }
`;
const V3_POOL_SEARCH_QUERY = `
  query SearchV3Pools($searchText: String!, $first: Int!) {
    pools(
      first: $first
      orderBy: totalValueLockedUSD
      orderDirection: desc
      where: {
        or: [
          { token0_: { symbol_contains_nocase: $searchText } }
          { token0_: { name_contains_nocase: $searchText } }
          { token1_: { symbol_contains_nocase: $searchText } }
          { token1_: { name_contains_nocase: $searchText } }
        ]
      }
    ) {
      id
      token0 {
        id
        symbol
        decimals
        name
      }
      token1 {
        id
        symbol
        decimals
        name
      }
      tickSpacing
      totalValueLockedUSD
    }
  }
`;
let gaugeCache = null;
let gaugeCacheTimestamp = 0;
const GAUGE_CACHE_TTL = 5 * 60 * 1e3;
const MAX_SEARCH_TERMS = 2;
const MIN_POOL_QUERY_LIMIT = 10;
async function fetchGaugesOnChain(chainId) {
  const now = Date.now();
  if (gaugeCache && now - gaugeCacheTimestamp < GAUGE_CACHE_TTL) {
    return gaugeCache;
  }
  const voterAddress = getContractAddress(chainId, "Voter");
  const voterAbi = getContractAbi(chainId, "Voter");
  if (!voterAddress || !voterAbi) {
    return /* @__PURE__ */ new Map();
  }
  try {
    const client = createPublicClient({
      chain: bsc,
      transport: http("https://bsc-dataseed.binance.org")
    });
    const lengthResult = await client.readContract({
      address: voterAddress,
      abi: voterAbi,
      functionName: "length",
      args: []
    });
    const gaugeCount = Number(lengthResult);
    if (gaugeCount === 0) {
      return /* @__PURE__ */ new Map();
    }
    const poolCalls = Array.from({ length: gaugeCount }, (_, i) => ({
      address: voterAddress,
      abi: voterAbi,
      functionName: "pools",
      args: [BigInt(i)]
    }));
    const poolsResults = await client.multicall({
      contracts: poolCalls,
      allowFailure: true
    });
    const poolAddresses = poolsResults.filter((r) => r.status === "success").map((r) => r.result.toLowerCase());
    const gaugeCalls = poolAddresses.map((poolAddr) => ({
      address: voterAddress,
      abi: voterAbi,
      functionName: "gauges",
      args: [poolAddr]
    }));
    const gaugesResults = await client.multicall({
      contracts: gaugeCalls,
      allowFailure: true
    });
    const gaugesMap = /* @__PURE__ */ new Map();
    gaugesResults.forEach((result, i) => {
      if (result.status === "success") {
        const poolAddr = poolAddresses[i];
        const gaugeAddr = result.result.toLowerCase();
        gaugesMap.set(poolAddr, gaugeAddr);
      }
    });
    gaugeCache = gaugesMap;
    gaugeCacheTimestamp = now;
    return gaugesMap;
  } catch (error) {
    logger.warn("usePoolSearchResults.ts", "fetchGaugesOnChain", "Failed to fetch gauges on-chain", error);
    return gaugeCache || /* @__PURE__ */ new Map();
  }
}
async function searchPools(searchText, limit = 10, chainId = UniverseChainId.Mainnet) {
  try {
    const subgraphs = getBscSubgraphs(chainId);
    const v2Subgraph = subgraphs.v2;
    const v3Subgraph = subgraphs.v3;
    const searchTerms = getPoolSearchTerms(searchText);
    const queryLimit = Math.max(limit, MIN_POOL_QUERY_LIMIT);
    const [v2Results, v3Results, gaugesMap] = await Promise.all([
      Promise.all(
        searchTerms.map(
          (term) => safeFetchSubgraph(
            v2Subgraph,
            {
              query: V2_POOL_SEARCH_QUERY,
              variables: { searchText: term, first: queryLimit },
              requestId: "searchPools.v2"
            },
            "v2 search"
          )
        )
      ),
      Promise.all(
        searchTerms.map(
          (term) => safeFetchSubgraph(
            v3Subgraph,
            {
              query: V3_POOL_SEARCH_QUERY,
              variables: { searchText: term, first: queryLimit },
              requestId: "searchPools.v3"
            },
            "v3 search"
          )
        )
      ),
      // Fetch gauges from on-chain instead of subgraph
      fetchGaugesOnChain(chainId)
    ]);
    const v2Pools = dedupePools(v2Results.flatMap((result) => result?.pairs ?? []));
    const v3Pools = dedupePools(v3Results.flatMap((result) => result?.pools ?? []));
    const poolScores = /* @__PURE__ */ new Map();
    const sortedV2Pools = sortPoolSearchResults(v2Pools, searchText, poolScores);
    const sortedV3Pools = sortPoolSearchResults(v3Pools, searchText, poolScores);
    return {
      v2Pools: sortedV2Pools.slice(0, limit),
      v3Pools: sortedV3Pools.slice(0, limit),
      gauges: gaugesMap,
      poolScores
    };
  } catch (error) {
    logger.warn("usePoolSearchResults.ts", "searchPools", "Failed to search pools", error);
    return { v2Pools: [], v3Pools: [], gauges: /* @__PURE__ */ new Map(), poolScores: /* @__PURE__ */ new Map() };
  }
}
function getPoolSearchTerms(searchText) {
  const normalized = searchText.trim().toLowerCase();
  if (!normalized) {
    return [];
  }
  if (isAddress(normalized)) {
    return [normalized];
  }
  const terms = normalized.split(/[\s/\\,;:|+_-]+/).map((term) => term.trim()).filter(Boolean);
  return Array.from(new Set(terms.length ? terms : [normalized])).slice(0, MAX_SEARCH_TERMS);
}
function normalizeSearchValue(value) {
  return value?.trim().toLowerCase() ?? "";
}
function compactSearchValue(value) {
  return value.replace(/[^a-z0-9]/g, "");
}
function scoreTextMatch(value, terms, fullQuery) {
  const normalized = normalizeSearchValue(value);
  if (!normalized) {
    return 0;
  }
  if (normalized === fullQuery) {
    return 1e3;
  }
  let score = 0;
  for (const term of terms) {
    if (normalized === term) {
      score += 700;
    } else if (normalized.startsWith(term)) {
      score += 350;
    } else if (normalized.includes(term)) {
      score += 120;
    }
  }
  return score;
}
function scorePoolSearchResult(pool, searchText) {
  const fullQuery = normalizeSearchValue(searchText);
  const compactQuery = compactSearchValue(fullQuery);
  const terms = getPoolSearchTerms(searchText);
  const token0Symbol = normalizeSearchValue(pool.token0.symbol);
  const token1Symbol = normalizeSearchValue(pool.token1.symbol);
  const pairNames = [
    `${token0Symbol}/${token1Symbol}`,
    `${token1Symbol}/${token0Symbol}`,
    `${token0Symbol}${token1Symbol}`,
    `${token1Symbol}${token0Symbol}`
  ];
  if (normalizeSearchValue(pool.id) === fullQuery) {
    return 1e4;
  }
  let score = 0;
  if (pairNames.includes(fullQuery) || pairNames.map(compactSearchValue).includes(compactQuery)) {
    score += 5e3;
  }
  const token0Score = scoreTextMatch(pool.token0.symbol, terms, fullQuery) + scoreTextMatch(pool.token0.name, terms, fullQuery) / 2;
  const token1Score = scoreTextMatch(pool.token1.symbol, terms, fullQuery) + scoreTextMatch(pool.token1.name, terms, fullQuery) / 2;
  score += token0Score + token1Score;
  if (terms.length > 1 && terms.every((term) => token0Symbol.includes(term) || token1Symbol.includes(term))) {
    score += 2e3;
  }
  return score;
}
function getPoolLiquidityUsd(pool) {
  const rawValue = "reserveUSD" in pool ? pool.reserveUSD : pool.totalValueLockedUSD;
  const parsed = parseFloat(rawValue || "0");
  return Number.isFinite(parsed) ? parsed : 0;
}
function dedupePools(pools) {
  const seen = /* @__PURE__ */ new Set();
  return pools.filter((pool) => {
    const key = pool.id.toLowerCase();
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}
function sortPoolSearchResults(pools, searchText, poolScores) {
  return pools.map((pool) => {
    const score = scorePoolSearchResult(pool, searchText);
    const liquidityUsd = getPoolLiquidityUsd(pool);
    poolScores.set(pool.id.toLowerCase(), score + Math.min(liquidityUsd, 1e9) / 1e9);
    return { pool, score, liquidityUsd };
  }).sort((a, b) => b.score - a.score || b.liquidityUsd - a.liquidityUsd).map(({ pool }) => pool);
}
function createCurrencyInfo(tokenData, chainId, logoFallback) {
  const token = new Token(chainId, tokenData.id, parseInt(tokenData.decimals, 10), tokenData.symbol, tokenData.name);
  return {
    currency: token,
    currencyId: `${chainId}-${tokenData.id.toLowerCase()}`,
    logoUrl: getTokenLogoUrl(tokenData.id) ?? logoFallback?.(tokenData.id) ?? null
  };
}
function transformV2PoolToOption(pool, chainId, gaugeAddress, logoFallback, feeDetails) {
  return {
    type: OnchainItemListOptionType.Pool,
    poolId: pool.id,
    chainId,
    token0CurrencyInfo: createCurrencyInfo(pool.token0, chainId, logoFallback),
    token1CurrencyInfo: createCurrencyInfo(pool.token1, chainId, logoFallback),
    protocolVersion: ProtocolVersion.V2,
    feeTier: pool.stable ? 500 : 3e3,
    feeDetails,
    gaugeAddress
  };
}
function transformV3PoolToOption(pool, chainId, gaugeAddress, logoFallback, feeDetails) {
  const tickSpacing = parseInt(pool.tickSpacing, 10);
  const feeTier = TICK_SPACING_TO_FEE[tickSpacing] || 3e3;
  return {
    type: OnchainItemListOptionType.Pool,
    poolId: pool.id,
    chainId,
    token0CurrencyInfo: createCurrencyInfo(pool.token0, chainId, logoFallback),
    token1CurrencyInfo: createCurrencyInfo(pool.token1, chainId, logoFallback),
    protocolVersion: ProtocolVersion.V3,
    feeTier,
    feeDetails,
    gaugeAddress
  };
}
export function usePoolSearchResults(searchQuery, chainFilter = null, limit = 10, enabled = true) {
  const chainId = chainFilter || UniverseChainId.Mainnet;
  const { getTokenListLogoUrl } = useTokenLists();
  const trimmedQuery = searchQuery?.trim();
  const searchEnabled = enabled && Boolean(trimmedQuery && trimmedQuery.length >= 2);
  const { feeDetailsByAddress, isLoading: feeDetailsLoading } = useTopazPoolFeeDetailsByAddress(chainId, searchEnabled);
  const {
    data: poolsData,
    isLoading,
    error
  } = useQuery({
    queryKey: ["pool-search", trimmedQuery, chainId, limit],
    queryFn: () => searchPools(trimmedQuery ?? "", limit, chainId),
    enabled: searchEnabled,
    // Only search with 2+ characters
    staleTime: 30 * 1e3
    // 30 seconds
  });
  const poolOptions = useMemo(() => {
    if (!poolsData) {
      return [];
    }
    const allPools = [];
    poolsData.v2Pools.forEach((pool) => {
      const gaugeAddress = poolsData.gauges.get(pool.id.toLowerCase());
      const feeDetails = feeDetailsByAddress.get(pool.id.toLowerCase());
      allPools.push(transformV2PoolToOption(pool, chainId, gaugeAddress, getTokenListLogoUrl, feeDetails));
    });
    poolsData.v3Pools.forEach((pool) => {
      const gaugeAddress = poolsData.gauges.get(pool.id.toLowerCase());
      const feeDetails = feeDetailsByAddress.get(pool.id.toLowerCase());
      allPools.push(transformV3PoolToOption(pool, chainId, gaugeAddress, getTokenListLogoUrl, feeDetails));
    });
    allPools.sort((a, b) => {
      const aScore = poolsData.poolScores.get(a.poolId.toLowerCase()) ?? 0;
      const bScore = poolsData.poolScores.get(b.poolId.toLowerCase()) ?? 0;
      return bScore - aScore;
    });
    return allPools.slice(0, limit);
  }, [poolsData, chainId, limit, getTokenListLogoUrl, feeDetailsByAddress]);
  return {
    data: poolOptions,
    loading: isLoading || feeDetailsLoading,
    error: error ?? void 0
  };
}
async function safeFetchSubgraph(endpoint, options, label) {
  try {
    return await fetchFromSubgraph(endpoint, options);
  } catch (error) {
    logger.warn("usePoolSearchResults.ts", "safeFetchSubgraph", `${label} failed`, error);
    return null;
  }
}
